import { rootApi } from '../rootApi';
import {
  ScanFinding,
  Replacement,
  Presentation,
  SummaryAction,
  WordLayoutPart,
  PresentationKind,
  PresentationPack,
  WordTemplateCheck,
  AnonymisationLevel,
  EmployerReplacement,
  PresentationSummary,
  PresentationSettings,
  PresentationTemplate,
  WordPlaceholderChanges,
  PresentationPublication,
  ExportPresentationResult,
  PresentationExportFormat,
  ExportPresentationRequest,
  CreatePresentationRequest,
  UpdatePresentationRequest,
  PresentationTemplateSummary,
  CreatePresentationPackRequest,
} from './presentations.types';

// Candidate presentations (JT1-1886). Tags are added here rather than in rootApi's shared list.
const api = rootApi.enhanceEndpoints({ addTagTypes: ['Presentations', 'Presentation', 'PresentationTemplates', 'PresentationEmployerReplacements', 'PresentationPacks', 'PresentationSettings'] });

export const presentationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCandidatePresentations: builder.query<PresentationSummary[], string>({
      query: (candidateId) => ({ url: `/my/candidates/${candidateId}/presentations` }),
      providesTags: (_result, _error, candidateId) => [{ type: 'Presentations', id: candidateId }],
    }),

    getPresentation: builder.query<Presentation, string>({
      query: (id) => ({ url: `/my/presentations/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'Presentation', id }],
    }),

    createPresentation: builder.mutation<Presentation, CreatePresentationRequest>({
      query: ({ candidateId, ...data }) => ({ url: `/my/candidates/${candidateId}/presentations`, method: 'POST', data }),
      invalidatesTags: (_result, _error, { candidateId }) => [{ type: 'Presentations', id: candidateId }],
    }),

    // The builder keeps its own copy while editing, so a save updates the cache rather than refetching.
    updatePresentation: builder.mutation<Presentation, UpdatePresentationRequest>({
      query: ({ id, ...data }) => ({ url: `/my/presentations/${id}`, method: 'PATCH', data }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(presentationsApi.util.upsertQueryData('getPresentation', id, data));
          dispatch(presentationsApi.util.invalidateTags([{ type: 'Presentations', id: data.organization_candidate_id }]));
        } catch {
          // The caller handles the error (409 shows the conflict banner).
        }
      },
    }),

    deletePresentation: builder.mutation<void, { id: string; candidateId: string }>({
      query: ({ id }) => ({ url: `/my/presentations/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { candidateId }) => [{ type: 'Presentations', id: candidateId }],
    }),

    duplicatePresentation: builder.mutation<Presentation, { id: string; candidateId: string }>({
      query: ({ id }) => ({ url: `/my/presentations/${id}/duplicate`, method: 'POST' }),
      invalidatesTags: (_result, _error, { candidateId }) => [{ type: 'Presentations', id: candidateId }],
    }),

    refreshPresentationBlock: builder.mutation<Presentation, { id: string; blockId: string; version: number }>({
      query: ({ id, blockId, version }) => ({ url: `/my/presentations/${id}/blocks/${blockId}/refresh`, method: 'POST', data: { version } }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(presentationsApi.util.upsertQueryData('getPresentation', id, data));
        } catch {
          // Shown by the caller.
        }
      },
    }),

    // An image uploaded through /upload/file, kept for presentations (image blocks, header and footer banners).
    // An AI draft of a text section; not saved, the builder puts it in the block (JT1-1886, A6).
    draftPresentationText: builder.mutation<{ html: string }, { id: string; blockId: string; action: SummaryAction; current_html: string }>({
      query: ({ id, blockId, ...data }) => ({ url: `/my/presentations/${id}/blocks/${blockId}/ai-draft`, method: 'POST', data }),
    }),

    claimPresentationImage: builder.mutation<{ file_id: string }, string>({
      query: (fileId) => ({ url: '/my/presentations/images', method: 'POST', data: { file_id: fileId } }),
    }),

    savePresentationAsTemplate: builder.mutation<PresentationTemplate, { id: string; name: string; is_default?: boolean }>({
      query: ({ id, ...data }) => ({ url: `/my/presentations/${id}/save-as-template`, method: 'POST', data }),
      invalidatesTags: ['PresentationTemplates'],
    }),

    suggestReplacements: builder.mutation<{ level: AnonymisationLevel; ai_used: boolean; ai_restricted?: boolean; replacements: Replacement[] }, { id: string; level: AnonymisationLevel }>({
      query: ({ id, level }) => ({ url: `/my/presentations/${id}/anonymisation/suggest`, method: 'POST', data: { level } }),
    }),

    scanPresentation: builder.mutation<{ findings: ScanFinding[]; ai_checked?: boolean }, { id: string; format?: PresentationExportFormat }>({
      query: ({ id, format }) => ({ url: `/my/presentations/${id}/scan`, method: 'POST', data: { format } }),
    }),

    exportPresentation: builder.mutation<ExportPresentationResult, ExportPresentationRequest>({
      query: ({ id, ...data }) => ({ url: `/my/presentations/${id}/exports`, method: 'POST', data }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(presentationsApi.util.invalidateTags([{ type: 'Presentation', id }, 'Presentations']));
        } catch {
          // A refused export (findings to acknowledge) is handled by the export dialog.
        }
      },
    }),

    getPresentationExportDownload: builder.mutation<{ url: string }, { id: string; exportId: string }>({
      query: ({ id, exportId }) => ({ url: `/my/presentations/${id}/exports/${exportId}/download` }),
    }),

    // Whether Word resumes and Word templates work here (the document converter is set up). Rarely changes.
    getPresentationCapabilities: builder.query<{ word_conversion: boolean }, void>({
      query: () => ({ url: '/my/presentation-capabilities' }),
      keepUnusedDataFor: 3600,
    }),

    // Agency Word templates (JT1-1887).
    getWordTemplatePlaceholders: builder.query<{ tag: string; repeats: boolean }[], void>({
      query: () => ({ url: '/presentation-templates/word/placeholders' }),
      keepUnusedDataFor: 3600,
    }),

    checkWordTemplate: builder.mutation<WordTemplateCheck, { file_id: string }>({
      query: (data) => ({ url: '/presentation-templates/word/check', method: 'POST', data }),
    }),

    // AI placeholder suggestions for an untagged Word layout (JT1-1887, B11).
    suggestWordPlaceholders: builder.mutation<{ placeholders: { paragraph: number; find: string; placeholder: string }[]; notes: { from: number; to: number; advice: string }[]; paragraphs: { index: number; text: string }[] }, { file_id: string }>({
      query: (data) => ({ url: '/presentation-templates/word/suggest', method: 'POST', data }),
    }),

    // The placeholder editor (staging feedback): an upload's text, then the choices written into a copy.
    getWordTemplateLayout: builder.mutation<{ parts: WordLayoutPart[] }, { file_id: string }>({
      query: (data) => ({ url: '/presentation-templates/word/layout', method: 'POST', data }),
    }),

    applyWordPlaceholders: builder.mutation<{ file_id: string; applied: number; check: WordTemplateCheck }, { file_id: string } & WordPlaceholderChanges>({
      query: (data) => ({ url: '/presentation-templates/word/apply', method: 'POST', data }),
    }),

    createWordTemplate: builder.mutation<PresentationTemplate, { file_id: string; name: string; kind: PresentationKind; is_default?: boolean }>({
      query: (data) => ({ url: '/presentation-templates/word', method: 'POST', data }),
      invalidatesTags: ['PresentationTemplates'],
    }),

    // The agency's employer wording for anonymised presentations (JT1-1887).
    getEmployerReplacements: builder.query<EmployerReplacement[], void>({
      query: () => ({ url: '/presentation-employer-replacements' }),
      providesTags: ['PresentationEmployerReplacements'],
    }),

    saveEmployerReplacement: builder.mutation<EmployerReplacement, { employer: string; replacement: string }>({
      query: (data) => ({ url: '/presentation-employer-replacements', method: 'PUT', data }),
      invalidatesTags: ['PresentationEmployerReplacements'],
    }),

    deleteEmployerReplacement: builder.mutation<void, string>({
      query: (id) => ({ url: `/presentation-employer-replacements/${id}`, method: 'DELETE' }),
      invalidatesTags: ['PresentationEmployerReplacements'],
    }),

    // A new version of a Word template; presentations already made keep theirs (JT1-1887).
    replaceWordTemplateFile: builder.mutation<PresentationTemplate, { key: string; file_id: string }>({
      query: ({ key, file_id }) => ({ url: `/presentation-templates/${key}/word-file`, method: 'POST', data: { file_id } }),
      invalidatesTags: ['PresentationTemplates'],
    }),

    useLatestWordTemplate: builder.mutation<Presentation, { id: string; version: number }>({
      query: ({ id, version }) => ({ url: `/my/presentations/${id}/word-template/latest`, method: 'POST', data: { version } }),
    }),

    // Shortlist packs and bulk runs, made in the background (JT1-1887). Polled while one is still being made.
    createPresentationPack: builder.mutation<PresentationPack, CreatePresentationPackRequest>({
      query: (data) => ({ url: '/my/presentation-packs', method: 'POST', data }),
      invalidatesTags: (_result, _error, { job_id }) => (job_id ? [{ type: 'PresentationPacks', id: job_id }] : ['PresentationPacks']),
    }),

    getPresentationPack: builder.query<PresentationPack, string>({
      query: (id) => ({ url: `/my/presentation-packs/${id}` }),
      providesTags: ['PresentationPacks'],
    }),

    getJobPresentationPacks: builder.query<PresentationPack[], string>({
      query: (jobId) => ({ url: `/my/jobs/${jobId}/presentation-packs` }),
      providesTags: (_result, _error, jobId) => [{ type: 'PresentationPacks', id: jobId }],
    }),

    getPresentationPackDownload: builder.mutation<{ url: string }, { id: string; acknowledge?: boolean }>({
      query: ({ id, acknowledge }) => ({ url: `/my/presentation-packs/${id}/download`, params: acknowledge ? { acknowledge: 'true' } : undefined }),
      invalidatesTags: ['PresentationPacks'],
    }),

    // An export on the application's client portal, and taking it down (JT1-1887, C7).
    publishPresentationExport: builder.mutation<PresentationPublication, { id: string; exportId: string; candidateId: string }>({
      query: ({ id, exportId }) => ({ url: `/my/presentations/${id}/exports/${exportId}/publish`, method: 'POST' }),
      invalidatesTags: (_result, _error, { id, candidateId }) => [{ type: 'Presentations', id: candidateId }, { type: 'Presentation', id }],
    }),

    revokePresentationPublication: builder.mutation<PresentationPublication, { id: string; publicationId: string; candidateId: string }>({
      query: ({ id, publicationId }) => ({ url: `/my/presentations/${id}/publications/${publicationId}/revoke`, method: 'POST' }),
      invalidatesTags: (_result, _error, { id, candidateId }) => [{ type: 'Presentations', id: candidateId }, { type: 'Presentation', id }],
    }),

    // An anonymised export emailed to client contacts from the recruiter's mailbox (JT1-1887, C13).
    sendPresentationMarketing: builder.mutation<{ id: string; export_id: string; contact_count: number; sent_at: string }, { id: string; exportId: string; contact_ids: string[]; subject: string; body: string }>({
      query: ({ id, exportId, ...data }) => ({ url: `/my/presentations/${id}/exports/${exportId}/send`, method: 'POST', data }),
    }),

    // The agency's presentation settings (JT1-1887): whether staff can change a presentation's design,
    // and how shortlist packs and comparisons open.
    getPresentationSettings: builder.query<PresentationSettings, void>({
      query: () => ({ url: '/presentation-templates/settings' }),
      providesTags: ['PresentationSettings'],
    }),

    updatePresentationSettings: builder.mutation<PresentationSettings, Partial<Pick<PresentationSettings, 'staff_can_edit_design' | 'packs'>>>({
      query: (data) => ({ url: '/presentation-templates/settings', method: 'PATCH', data }),
      // An open builder is told again whether its Design tab shows.
      invalidatesTags: ['PresentationSettings', 'Presentation'],
    }),

    getPresentationTemplates: builder.query<PresentationTemplateSummary[], void>({
      query: () => ({ url: '/presentation-templates' }),
      providesTags: ['PresentationTemplates'],
    }),

    getPresentationTemplate: builder.query<PresentationTemplate, string>({
      query: (key) => ({ url: `/presentation-templates/${key}` }),
      providesTags: (_result, _error, key) => [{ type: 'PresentationTemplates', id: key }],
    }),

    duplicatePresentationTemplate: builder.mutation<PresentationTemplate, { key: string; name?: string; is_default?: boolean }>({
      query: ({ key, ...data }) => ({ url: `/presentation-templates/${key}/duplicate`, method: 'POST', data }),
      invalidatesTags: ['PresentationTemplates'],
    }),

    updatePresentationTemplate: builder.mutation<PresentationTemplate, { id: string; name?: string; definition?: PresentationTemplate['definition']; is_default?: boolean; client_company_id?: string | null }>({
      query: ({ id, ...data }) => ({ url: `/presentation-templates/${id}`, method: 'PATCH', data }),
      invalidatesTags: ['PresentationTemplates'],
    }),

    // Hides a template from staff, or offers it again (JT1-1887, admins).
    setPresentationTemplateHidden: builder.mutation<PresentationSettings, { key: string; hidden: boolean }>({
      query: ({ key, hidden }) => ({ url: `/presentation-templates/${key}/visibility`, method: 'PATCH', data: { hidden } }),
      invalidatesTags: ['PresentationTemplates', 'PresentationSettings'],
    }),

    deletePresentationTemplate: builder.mutation<void, string>({
      query: (id) => ({ url: `/presentation-templates/${id}`, method: 'DELETE' }),
      invalidatesTags: ['PresentationTemplates'],
    }),
  }),
});

export const {
  useGetCandidatePresentationsQuery,
  useGetPresentationQuery,
  useCreatePresentationMutation,
  useUpdatePresentationMutation,
  useDeletePresentationMutation,
  useDuplicatePresentationMutation,
  useRefreshPresentationBlockMutation,
  useClaimPresentationImageMutation,
  useDraftPresentationTextMutation,
  useSavePresentationAsTemplateMutation,
  useSuggestReplacementsMutation,
  useScanPresentationMutation,
  useExportPresentationMutation,
  useGetPresentationExportDownloadMutation,
  useGetPresentationTemplatesQuery,
  useGetPresentationSettingsQuery,
  useUpdatePresentationSettingsMutation,
  useSetPresentationTemplateHiddenMutation,
  useGetWordTemplatePlaceholdersQuery,
  useCheckWordTemplateMutation,
  useCreateWordTemplateMutation,
  useSuggestWordPlaceholdersMutation,
  useApplyWordPlaceholdersMutation,
  useGetWordTemplateLayoutMutation,
  useReplaceWordTemplateFileMutation,
  useUseLatestWordTemplateMutation,
  useGetEmployerReplacementsQuery,
  useSaveEmployerReplacementMutation,
  useDeleteEmployerReplacementMutation,
  useGetPresentationCapabilitiesQuery,
  useGetPresentationTemplateQuery,
  useDuplicatePresentationTemplateMutation,
  useUpdatePresentationTemplateMutation,
  useDeletePresentationTemplateMutation,
  useCreatePresentationPackMutation,
  useGetJobPresentationPacksQuery,
  useGetPresentationPackQuery,
  useGetPresentationPackDownloadMutation,
  usePublishPresentationExportMutation,
  useRevokePresentationPublicationMutation,
  useSendPresentationMarketingMutation,
} = presentationsApi;
